import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import type { Invoice, InvoiceNew, InvoiceQuery } from "./invoice";
import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsError, ToastOptionsSuccess } from "@modernary/mui/components/toasts";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import invoiceService from "./invoiceService";
import type { AxiosResponse } from "axios";

// Globals
const URL = "invoices";
const ENTITY_NAME = "Invoice";

const axiosParams = {
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Invoice>(apiHosts.AQMS, axiosParams);

// Functions
// GET
const getInvoices = (params: InvoiceQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

// GET
type InvoiceIds = {
  id?: string | number;
};
const getNewInvoice = async (ids: InvoiceIds) => {
  const defaultApp = {
    data: {
      ...invoiceService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<InvoiceNew, any>;

  return defaultApp;
};

// GET
const getInvoiceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

// POST
const createInvoice = (invoice: Invoice, config: PagingApiAndNotifierConfig) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.post(URL, invoice, {
    successToastCb,
    errorToastCb,
  });
};

// PUT
const updateInvoice = (invoice: Invoice, config: PagingApiAndNotifierConfig) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.put(`${URL}/${invoice.id}`, invoice, {
    successToastCb,
    errorToastCb,
  });
};

// HELPER / EXTENDED
const saveInvoice = (invoice: Invoice, config = {}) => {
  if (invoice.id) {
    return updateInvoice(invoice, config);
  } else {
    return createInvoice(invoice, config);
  }
};

// DELETE
const deleteInvoiceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successToastCb,
    errorToastCb,
  });
};

export { getInvoices, getNewInvoice, getInvoiceById, createInvoice, updateInvoice, saveInvoice, deleteInvoiceById };
